import React, { useCallback, useMemo } from 'react';
import { Treemap } from '@visx/hierarchy';
import { withParentSize } from '@visx/responsive';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { faChartNetwork } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from 'js/components';
import {
  annotateCapabilityTree,
  countVisibleCapabilityNodes,
} from 'js/utils/capability-treemap-scores';
import CapabilityNode from './CapabilityNode';
import CapabilityTooltip from './CapabilityTooltip';
import {
  DEFAULT_TILE_AREA,
  DEFAULT_MIN_HEIGHT,
  buildCapabilityRoot,
} from './capabilityTreemapUtils';
import styles from './CapabilityTreemap.module.scss';

function CapabilityTreemapChart({
  parentWidth,
  capabilities,
  scoresByCapability,
  selectedElementKey,
  selectedElementName,
  selectedElementPaletteIndex,
  hasCapabilitiesInSet = false,
  tileArea = DEFAULT_TILE_AREA,
  minHeight = DEFAULT_MIN_HEIGHT,
  padding = 4,
  minLabelWidth = 20,
  minLabelHeight = 58,
}) {
  const width = Math.max(0, parentWidth || 0);
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const handleCapabilityHover = useCallback(
    (event, capabilityData) => {
      const coords = localPoint(event.target.ownerSVGElement, event);
      if (!coords) {
        return;
      }
      showTooltip({
        tooltipLeft: coords.x,
        tooltipTop: coords.y,
        tooltipData: {
          ...capabilityData,
          elementName: selectedElementName,
          elementPaletteIndex: selectedElementPaletteIndex,
        },
      });
    },
    [showTooltip, selectedElementName, selectedElementPaletteIndex]
  );

  const annotatedCapabilities = useMemo(
    () =>
      annotateCapabilityTree(
        capabilities,
        scoresByCapability,
        selectedElementKey,
        tileArea
      ),
    [capabilities, scoresByCapability, selectedElementKey, tileArea]
  );

  const treeRoot = useMemo(
    () => buildCapabilityRoot(annotatedCapabilities),
    [annotatedCapabilities]
  );

  const visibleNodeCount = useMemo(
    () => countVisibleCapabilityNodes(capabilities),
    [capabilities]
  );

  if (width <= 0) {
    return null;
  }

  const hasRenderableNodes = treeRoot.children?.length > 0;
  const height = Math.max(
    minHeight,
    visibleNodeCount > 0 ? (visibleNodeCount * tileArea) / width : minHeight
  );
  const fullChartWidth = width + 2 * padding;

  if (!hasRenderableNodes) {
    return (
      <div className={styles['treemap-frame']} style={{ minHeight: height }}>
        <div className="media text-muted mt-4 mb-4">
          <Icon icon={faChartNetwork} size="4x" className="mr-3" fixedWidth />
          <div className="media-body">
            {hasCapabilitiesInSet ? (
              <>
                <p>No capabilities selected.</p>
                <p>
                  Select capabilities in the filter above to show the treemap.
                </p>
              </>
            ) : (
              <>
                <p>
                  This capability set doesn&apos;t have any capabilities yet.
                </p>
                <p>
                  Add capabilities on the Hierarchy tab to see results here.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles['treemap-frame']}
      style={{
        minHeight: height,
        margin: `0 -${padding}px`,
      }}
    >
      <svg
        width={fullChartWidth}
        height={height}
        viewBox={`0 0 ${fullChartWidth} ${height}`}
      >
        <Treemap
          root={treeRoot}
          size={[fullChartWidth, height]}
          padding={padding}
          round
        >
          {(treemap) => (
            <g>
              {treemap
                .descendants()
                .filter((node) => !node.children)
                .map((node) => (
                  <CapabilityNode
                    key={
                      node.data.id || node.data.name || `${node.x0}-${node.y0}`
                    }
                    node={node}
                    minLabelWidth={minLabelWidth}
                    minLabelHeight={minLabelHeight}
                    onCapabilityHover={handleCapabilityHover}
                    onCapabilityLeave={hideTooltip}
                  />
                ))}
            </g>
          )}
        </Treemap>
      </svg>
      <CapabilityTooltip
        tooltipOpen={tooltipOpen}
        tooltipData={tooltipData}
        tooltipLeft={tooltipLeft}
        tooltipTop={tooltipTop}
      />
    </div>
  );
}

export default withParentSize(CapabilityTreemapChart);
